import React from 'react';
import { Calendar, MapPin, Tag, Hash, Fingerprint } from 'lucide-react';
import { ACAO_LABEL, formatarChaveDetalhe, formatarValorDetalhe } from '../../utils/auditLabels';
import ModalRodape from './ModalRodape';

const linha = (Icone, label, valor) => (
  <div className="flex items-start gap-2.5 text-sm">
    <Icone className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
    <div className="min-w-0">
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-gray-800 break-words">{valor}</p>
    </div>
  </div>
);

// Uma linha de "detalhes": chave traduzida + valor. Quando o valor é um
// objeto aninhado (ex: meta do upload de foto), desce um nível e repete o
// tratamento — nunca cai pra JSON.stringify cru, mesmo dentro de dentro.
const DetalheLinha = ({ chave, valor, nivel = 0 }) => {
  const label = formatarChaveDetalhe(chave);

  if (valor !== null && typeof valor === 'object' && !Array.isArray(valor)) {
    const subChaves = Object.keys(valor);
    return (
      <div className={nivel > 0 ? 'ml-3 pl-3 border-l-2 border-gray-200' : ''}>
        <p className="text-xs font-semibold text-gray-500 mt-2.5 mb-1">{label}</p>
        {subChaves.length === 0 ? (
          <p className="text-sm text-gray-400 italic">—</p>
        ) : (
          subChaves.map((k) => <DetalheLinha key={k} chave={k} valor={valor[k]} nivel={nivel + 1} />)
        )}
      </div>
    );
  }

  return (
    <div className={`flex items-baseline justify-between gap-3 text-sm py-1.5 border-b border-gray-100 last:border-0 ${nivel > 0 ? 'pl-0' : ''}`}>
      <span className="text-gray-500 flex-shrink-0">{label}</span>
      <span className="text-gray-800 font-medium text-right break-all">{formatarValorDetalhe(valor)}</span>
    </div>
  );
};

// Ver detalhes completos de uma entrada do log — a versão de card na lista
// já mostra um resumo (3 primeiras chaves de "detalhes"); aqui é o dado
// completo, mas ainda traduzido (chave → rótulo, valor → texto legível),
// nunca o JSON bruto. O bloco de "detalhes" tem rolagem própria (max-h +
// overflow-y) — algumas ações (ex: atualizar_enquete) gravam objetos
// grandes, e sem isso o conteúdo empurrava o modal inteiro pra fora da tela
// em vez de rolar contido.
const DetalhesLogModal = ({ entrada, onFechar }) => {
  const detalhes = entrada.detalhes;
  const chaves = detalhes && typeof detalhes === 'object' ? Object.keys(detalhes) : [];

  return (
    <div className="space-y-4">
      <div className="bg-gray-50 rounded-xl p-4">
        <p className="text-sm text-gray-800 leading-snug">
          <span className="font-semibold">{entrada.admin_username}</span>{' '}
          {ACAO_LABEL[entrada.acao] || entrada.acao}
          {entrada.entidade_id ? ` #${entrada.entidade_id}` : ''}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {linha(Fingerprint, 'ID do log', `#${entrada.id}`)}
        {linha(Calendar, 'Quando', new Date(entrada.data_criacao).toLocaleString('pt-BR'))}
        {linha(Tag, 'Ação (bruta)', entrada.acao)}
        {linha(Hash, 'Entidade', `${entrada.entidade}${entrada.entidade_id ? ` #${entrada.entidade_id}` : ''}`)}
        {linha(MapPin, 'IP', entrada.ip_endereco || 'desconhecido')}
      </div>

      <div>
        <p className="text-xs text-gray-400 mb-1.5">Detalhes completos</p>
        {typeof detalhes === 'string' && detalhes ? (
          <p className="text-sm text-gray-700 bg-gray-50 border border-gray-100 rounded-lg p-3">{detalhes}</p>
        ) : chaves.length > 0 ? (
          <div className="bg-gray-50 border border-gray-100 rounded-lg px-3 py-1 overflow-y-auto max-h-64">
            {chaves.map((chave) => (
              <DetalheLinha key={chave} chave={chave} valor={detalhes[chave]} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400 italic">Nenhum detalhe adicional registrado.</p>
        )}
      </div>

      <ModalRodape>
        <button
          onClick={onFechar}
          className="text-sm font-semibold text-gray-700 bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50 rounded-lg px-5 py-2.5 shadow-sm transition-colors"
        >
          Fechar
        </button>
      </ModalRodape>
    </div>
  );
};

export default DetalhesLogModal;
